import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import "../styles/QuizPageEnhanced.css";
/* ── Add these rules to QuizPageEnhanced.css ──────────────────────────────
.coding-question        { display: flex; flex-direction: column; gap: 10px; }
.coding-label           { font-size: 12px; font-weight: 700; text-transform: uppercase;
                          letter-spacing: .06em; color: var(--text-3); margin: 0; }
.coding-pre             { background: #0f172a; color: #e2e8f0; padding: 14px 16px;
                          border-radius: 8px; font-size: 13px; overflow-x: auto;
                          white-space: pre; font-family: 'Fira Code', monospace; margin: 0; }
.coding-editor          { width: 100%; min-height: 180px; padding: 12px 14px;
                          border: 1.5px solid var(--border); border-radius: 8px;
                          font-family: 'Fira Code', 'Cascadia Code', monospace; font-size: 13px;
                          background: #0f172a; color: #e2e8f0; resize: vertical;
                          outline: none; line-height: 1.6; box-sizing: border-box; }
.coding-editor:focus    { border-color: var(--accent); }
.coding-hint            { font-size: 13px; color: var(--text-2); background: #fefce8;
                          border: 1px solid #fde68a; border-radius: 6px;
                          padding: 8px 12px; margin: 0; }
.fill-blank-pre         { white-space: pre-wrap; line-height: 2; }
.fill-blank-input       { display: inline-block; background: #1e3a5f; color: #93c5fd;
                          border: none; border-bottom: 2px solid #60a5fa;
                          font-family: inherit; font-size: inherit; padding: 0 6px;
                          min-width: 120px; outline: none; border-radius: 2px; }
.fill-blank-input:focus { border-bottom-color: #38bdf8; background: #1e3a5f; }
────────────────────────────────────────────────────────────────────────── */
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function QuizPage(props) {
  const { program_name,course_name,quiz_file_name,id: paramId} = useParams();
  
  const location = useLocation();
  const id = props?.id ?? paramId;

  // ── Mode flag ─────────────────────────────────────────────
  // Pass ?mode=practice in the URL  →  practice mode
  // Or set  props.mode="practice"   →  practice mode (embed usage)
  // Default (assessment_quiz route) →  assessment mode
  const urlMode = new URLSearchParams(location.search).get("mode");
  const isPracticeMode = (props?.mode ?? urlMode) === "practice";

  const [module, setModule] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [progress, setProgress] = useState(0);

  const [totalTime, setTotalTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  const [userAnswers, setUserAnswers] = useState([]);
  const [isReview, setIsReview] = useState(false);
  const [showOnlyWrong, setShowOnlyWrong] = useState(false);
  const [showOnlySkipped, setShowOnlySkipped] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "wrong" | "skipped"
  const [finalSkipped, setFinalSkipped] = useState(new Set());

  // ── Skip & Revisit ─────────────────────────────────────
  const [skippedQuestions, setSkippedQuestions] = useState(new Set());
  const [isRevisiting, setIsRevisiting] = useState(false);
  const [skippedQueue, setSkippedQueue] = useState([]);

  // ── Confirmation dialog ────────────────────────────────
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Time warning toast ─────────────────────────────────
  const [timeWarning, setTimeWarning] = useState(null);
  const [shownWarnings, setShownWarnings] = useState(new Set());

  // ── Practice mode inline feedback (shown after each submit) ──
  const [practiceResult, setPracticeResult] = useState(null); // { isCorrect, correct, explanation, isCoding }

  // ── Prior graded score check — drives the "Non-Graded Retake" flow ──
  // If a graded score already exists in Firestore for this module, this
  // session is automatically treated as a non-graded retake: nothing gets
  // (re)written to Firestore, and the intro/review screens say so.
  const [priorScore, setPriorScore] = useState(null); // { score, percent, completedOn } | null
  const [priorScoreChecked, setPriorScoreChecked] = useState(false);
  const isRetake = !isPracticeMode && !!priorScore;
  const effectivePracticeMode = isPracticeMode || isRetake;

  // ── Coding question state ───────────────────────────────
  const [codeInput, setCodeInput] = useState("");
  const [codeResult, setCodeResult] = useState(null); // { passed, message }

  // ── Fill-in-the-blank state ─────────────────────────────
  // One independent string per "___" blank in the question's code, so
  // multi-blank questions (each with its own answer) work correctly.
  const [blankInputs, setBlankInputs] = useState([]);

  const quizJsonPath =
  props?.quizPath ??
  (program_name
    ? `/mcqs/${program_name}/${course_name}/${quiz_file_name}.json`
    : "/questions.json");

  useEffect(() => {
    if (!id) return;
    fetch(quizJsonPath)
      .then((r) => r.json())
      .then((data) => {
        let mod = null;
        const mid = parseInt(id);
        if (Array.isArray(data.modules)) {
          mod = data.modules.find((m) => m.id === mid) || data.modules[0];
        } else if (Array.isArray(data)) {
          mod = { id: mid, title: "Quiz", duration: data.duration, questions: data };
        } else if (data.questions) {
          mod = { id: mid, title: data.title || "Quiz", duration: data.duration, questions: data.questions };
        }
        setModule(mod);
        setTotalTime(mod.duration || 600);
        setTimeLeft(mod.duration || 600);
      })
      .catch(() => setModule(null));
  }, [quizJsonPath, id]);

  useEffect(() => {
    if (!module) return;
    setQuizStarted(false);
    setCurrentQ(0); setSelected(null); setFeedback(null);
    setScore(0); setUserAnswers([]); setIsReview(false);
    setShowOnlyWrong(false); setSkippedQuestions(new Set());
    setIsRevisiting(false); setSkippedQueue([]);
    setShowConfirm(false); setTimeWarning(null); setShownWarnings(new Set());
    setPracticeResult(null);
    setCodeInput(""); setCodeResult(null); setBlankInputs([]);
  }, [module]);

  useEffect(() => {
    if (module) setProgress(((currentQ + 1) / module.questions.length) * 100);
  }, [currentQ, module]);

  // ── Size blankInputs to the current question's blank count ─────────────
  // Runs whenever the current question changes (next, skip, revisit, etc.)
  // so each blank always starts empty and independently tracked.
  useEffect(() => {
    if (!module) return;
    const question = module.questions[currentQ];
    if (question?.type === "fill-in-blank") {
      const templ = question.code || question.codeTemplate || (question.question || "").replace("______", "___");
      const blankCount = (templ.match(/___/g) || []).length;
      setBlankInputs(Array(blankCount).fill(""));
    } else {
      setBlankInputs([]);
    }
  }, [currentQ, module]);

  function updateBlank(idx, value) {
    setBlankInputs((prev) => {
      const copy = [...prev];
      copy[idx] = value;
      return copy;
    });
  }

  // ── Check Firestore for an already-graded score on this module ─────────
  // Explicit practice-mode embeds never write scores, so there's nothing to
  // check. For the assessment route, if a score is already on file, this
  // attempt is automatically treated as a non-graded retake.
  useEffect(() => {
    if (!module || isPracticeMode) { setPriorScoreChecked(true); return; }
    let cancelled = false;
    setPriorScoreChecked(false);
    (async () => {
      try {
        const orgCode = localStorage.getItem("orgCode");
        const studentId = localStorage.getItem("studentId");
        if (!orgCode || !studentId || !module.courseName || !module.moduleId) {
          if (!cancelled) { setPriorScore(null); setPriorScoreChecked(true); }
          return;
        }
        const studentRef = doc(db, "CorporateClients", orgCode, "studentInfo", String(studentId));
        const snap = await getDoc(studentRef);
        const moduleMarks = snap.exists()
          ? snap.data()?.marks?.[module.courseName]?.[module.moduleId]
          : null;
        if (!cancelled) {
          if (moduleMarks && moduleMarks.quiz !== undefined && moduleMarks.quiz !== null) {
            setPriorScore({
              score: moduleMarks.quiz,
              percent: moduleMarks.quizPercentage,
              completedOn: moduleMarks.quizCompletedOn,
            });
          } else {
            setPriorScore(null);
          }
          setPriorScoreChecked(true);
        }
      } catch (e) {
        console.error("Error checking prior quiz score:", e);
        if (!cancelled) { setPriorScore(null); setPriorScoreChecked(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [module, isPracticeMode]);

  // ── Timer (only runs after quiz is started, not in practice mode) ─────────
  useEffect(() => {
    if (!module || !quizStarted || timeLeft <= 0 || isReview || effectivePracticeMode) return;
    const timer = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timer); finishQuiz(); return 0; }

        if (t === 300 && totalTime > 300) {
          setShownWarnings((prev) => {
            if (!prev.has("5min")) {
              setTimeWarning("5min");
              setTimeout(() => setTimeWarning(null), 4000);
              return new Set([...prev, "5min"]);
            }
            return prev;
          });
        }
        if (t === 60) {
          setShownWarnings((prev) => {
            if (!prev.has("1min")) {
              setTimeWarning("1min");
              setTimeout(() => setTimeWarning(null), 4000);
              return new Set([...prev, "1min"]);
            }
            return prev;
          });
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [module, quizStarted, timeLeft, isReview, effectivePracticeMode]);

  if (!module || (!isPracticeMode && !priorScoreChecked)) return <div className="center">Loading…</div>;

  const passThreshold = module.passThreshold ?? 75;
  const defaultPoints = parseFloat(module.points ?? 1);
  const maxPoints = module.questions.reduce((sum, question) => sum + parseFloat(question.points ?? defaultPoints), 0);
  const q = module.questions[currentQ] ?? {};
  const fillTemplate =
  q.type === "fill-in-blank"
    ? (q.code || q.codeTemplate || q.question.replace("______", "___"))
    : "";
  const isLastQuestion = isRevisiting
    ? skippedQueue.indexOf(currentQ) === skippedQueue.length - 1
    : currentQ + 1 === module.questions.length;

  // ── Firestore save ──────────────────────────────────────
  const saveQuizScore = async (finalScore) => {
    if (effectivePracticeMode) return; // not graded — don't overwrite original score
    try {
      const orgCode   = localStorage.getItem("orgCode");
      const studentId = localStorage.getItem("studentId");
      if (!orgCode || !studentId || !module) return;
      const studentRef = doc(db, "CorporateClients", orgCode, "studentInfo", String(studentId));
      await updateDoc(studentRef, {
        [`marks.${module.courseName}.${module.moduleId}.quiz`]: finalScore,
        [`marks.${module.courseName}.${module.moduleId}.quizPercentage`]:
          Math.round((finalScore / maxPoints) * 100),
        [`marks.${module.courseName}.${module.moduleId}.quizCompletedOn`]:
          new Date().toISOString(),
      });
    } catch (e) { console.error("Error saving quiz score:", e); }
  };

  // ── Skip helpers ────────────────────────────────────────
  function findNextUnanswered(startFrom, skippedSet = skippedQuestions) {
    for (let i = startFrom; i < module.questions.length; i++) {
      if (!userAnswers[i] && !skippedSet.has(i)) return i;
    }
    return null;
  }

  function startRevisitMode(currentSkipped) {
    const arr = [...currentSkipped].sort((a, b) => a - b);
    if (arr.length === 0) { finishQuiz(); return; }
    setSkippedQueue(arr); setIsRevisiting(true);
    setCurrentQ(arr[0]); setSelected(null); setFeedback(null);
  }

function skipQuestion() {
  const updated = new Set([...skippedQuestions, currentQ]);
  setSkippedQuestions(updated);

  setSelected(null);
  setFeedback(null);
  setPracticeResult(null); // add this
  setCodeInput("");        // add this
  setBlankInputs([]);

  const next = findNextUnanswered(currentQ + 1);
  next !== null ? setCurrentQ(next) : startRevisitMode(updated);
}

function navigateToSkipped(idx) {
  if (!skippedQuestions.has(idx)) return;

  const arr = [...skippedQuestions].sort((a, b) => a - b);

  setSkippedQueue(arr);
  setIsRevisiting(true);
  setCurrentQ(idx);

  setSelected(null);
  setFeedback(null);
  setPracticeResult(null); // important
  setCodeInput("");        // important
  setBlankInputs([]);
}

  // ── Answer submission ───────────────────────────────────
  function advanceAfterAnswer(updatedSkipped = skippedQuestions) {
    setSelected(null); setFeedback(null);
   if (isRevisiting) {
  const idx = skippedQueue.indexOf(currentQ);
  const nextInQueue = skippedQueue[idx + 1];

  if (nextInQueue !== undefined) {
    setCurrentQ(nextInQueue);
  } else {
    const next = findNextUnanswered(0, updatedSkipped) // check whole quiz

    if (next !== null) {
      setIsRevisiting(false);
      setCurrentQ(next);
    } else if (updatedSkipped.size > 0) {
      startRevisitMode(updatedSkipped);
    } else {
      finishQuiz();
    }
  }
} else {
  const next = findNextUnanswered(currentQ + 1, updatedSkipped);

  if (next !== null) {
    setCurrentQ(next);
  } else if (updatedSkipped.size > 0) {
    startRevisitMode(updatedSkipped);
  } else {
    finishQuiz();
  }
}
  }

  function submitAnswer() {
    if (feedback) return;
    // In practice mode, if result already shown, "Next" button handles advance
    if (effectivePracticeMode && practiceResult) return;

    let answerToCheck = selected;
    let isCorrect;

    if (q.type === "coding") {
      const userCode = codeInput.trim();
      if (!userCode) return;
      answerToCheck = userCode;
      isCorrect = userCode === String(q.answer).trim();
    } else if (q.type === "fill-in-blank") {
      if (blankInputs.length === 0 || blankInputs.some((b) => !b.trim())) return;
      const expected = Array.isArray(q.answer) ? q.answer : [q.answer];
      const trimmedInputs = blankInputs.map((b) => b.trim());
      isCorrect =
        trimmedInputs.length === expected.length &&
        trimmedInputs.every((val, i) => val === String(expected[i]).trim());
      answerToCheck = trimmedInputs.join(", ");
    } else {
      isCorrect = Array.isArray(q.answer)
        ? q.answer.includes(selected)
        : selected === q.answer;
    }

    const questionPoints = parseFloat(q.points ?? defaultPoints);

    setUserAnswers((prev) => {
      const copy = [...prev];
      copy[currentQ] = {
        question: q.question,
        code: q.code || null,
        selected: answerToCheck,
        correct: Array.isArray(q.answer) ? q.answer.join(", ") : q.answer,
        explanation: q.explanation,
        isCorrect,
        isCoding: q.type === "coding" || q.type === "fill-in-blank",
        questionNumber: currentQ + 1,
        points: questionPoints,
        earnedPoints: isCorrect ? questionPoints : 0,
      };
      return copy;
    });

    const updatedSkipped = new Set(skippedQuestions);
updatedSkipped.delete(currentQ);
setSkippedQuestions(updatedSkipped);

    if (isCorrect) setScore((s) => Math.round((s + questionPoints) * 1000) / 1000);

    if (effectivePracticeMode) {
      // Show inline result — user clicks Next to advance
      setPracticeResult({
        isCorrect,
        correct: Array.isArray(q.answer) ? q.answer.join(", ") : q.answer,
        explanation: q.explanation,
        isCoding: q.type === "coding" || q.type === "fill-in-blank",
        selected: answerToCheck,
      });
    } else {
      setCodeInput(""); setCodeResult(null); setBlankInputs([]);
      advanceAfterAnswer(updatedSkipped);
    }
  }

function practiceNext() {
  setPracticeResult(null);
  setCodeInput("");
  setCodeResult(null);
  setBlankInputs([]);
  advanceAfterAnswer();
}

  // ── Confirmation before finish ──────────────────────────
  function tryFinish() {
    const answeredCount = userAnswers.filter(Boolean).length;
    const unanswered = module.questions.length - answeredCount - skippedQuestions.size;
    if (skippedQuestions.size + unanswered > 0) {
      setShowConfirm(true);
    } else {
      finishQuiz();
    }
  }

async function finishQuiz() {
  setShowConfirm(false);

  const allSkipped = new Set(skippedQuestions);

  module.questions.forEach((_, idx) => {
    if (!userAnswers[idx]) {
      allSkipped.add(idx);
    }
  });

  setFinalSkipped(allSkipped);

  await saveQuizScore(score);
  setIsReview(true);
}

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const answeredCount = userAnswers.filter(Boolean).length;
  const unansweredCount = module.questions.length - answeredCount - skippedQuestions.size;
  const totalPending = skippedQuestions.size + unansweredCount;

  /* ═══════════════════ INTRO SCREEN ════════════════════ */
  if (!quizStarted && !isPracticeMode) {
    const durationMins = Math.round((module.duration || 600) / 60);
    const total = module.questions.length;
    const passScore = Math.ceil(total * passThreshold / 100);

    return (
      <div className="quiz-page">
        <div className="quiz-topbar">
          <span className="quiz-topbar-title">{module.title}</span>
        </div>

        <div className="quiz-container">
          <div className="intro-card">

            <div className="intro-header">
              
              <h1 className="intro-title">{module.title}</h1>
              {module.subtitle && <p className="intro-subtitle">{module.subtitle}</p>}
            </div>

            {/* Mode banner */}
            {isRetake ? (
              <div className="intro-practice-banner">
                📝 <strong>Non-Graded Retake</strong> — You already completed this quiz
                {priorScore?.percent != null ? ` and scored ${priorScore.percent}%` : ""}.
                This attempt is for practice only — it won't be saved and won't change your recorded score.
              </div>
            ) : null}

            {/* Description */}
            {module.description && (
              <div className="intro-section">
                <h2 className="intro-section-title">Description</h2>
                <p className="intro-section-body">{module.description}</p>
              </div>
            )}

            {/* Topics covered */}
            {module.topics && module.topics.length > 0 && (
              <div className="intro-section">
                <h2 className="intro-section-title">Topics Covered</h2>
                <ol className="intro-topics">
                  {module.topics.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ol>
              </div>
            )}

         
            {/* Pass target detail */}
            <div className="intro-pass-banner">
              🎯 <strong>Score {passThreshold}%</strong> to pass · <strong>{maxPoints} total points</strong> 
              {module.passMessage && <span> {module.passMessage}</span>}
            </div>

            <button className="quiz-btn primary intro-start-btn" onClick={() => setQuizStarted(true)}>
              {isRetake ? "Retake Quiz (Non-Graded) →" : "Start Quiz →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════ REVIEW SCREEN ═══════════════════ */
  if (isReview) {
    const total   = module.questions.length;
    const percent = maxPoints > 0 ? Math.round((score / maxPoints) * 100) : 0;
    const wrong   = userAnswers.filter(a => a != null && !a.isCorrect).length;
    const correct = userAnswers.filter(a => a != null && a.isCorrect).length;
    const skipped = finalSkipped.size;
    const passed  = percent >= passThreshold;

    return (
      <div className="quiz-page">
        <div className="quiz-topbar">
          <span className="quiz-topbar-title">{module.title}</span>
          <span style={{ color: "#94a3b8", fontSize: 13 }}>{effectivePracticeMode ? (isPracticeMode ? "Practice Summary" : "Retake Summary") : "Review"}</span>
        </div>

        <div className="quiz-container">
          <div className="quiz-inner">

            <div className="quiz-card" style={{ textAlign: "center" }}>
              {effectivePracticeMode ? (
                /* ── Practice / Retake summary header ── */
                <>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 14px", borderRadius: 9999,
                    background: "#eff6ff", border: "1px solid #bfdbfe",
                    color: "#1d4ed8", fontSize: 12, fontWeight: 700,
                    letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 12,
                  }}>
                    {isPracticeMode ? "🎓 Practice Mode" : "📝 Non-Graded Retake"}
                  </div>
                  <h2 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 4px", color: "var(--text-1)" }}>
                    {isPracticeMode ? "Practice Complete!" : "Retake Complete!"}
                  </h2>
                  <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 0 }}>{module.title}</p>

                  <div className="review-stats" style={{ marginTop: 24 }}>
                    <div>
                      <p className="review-stat-value" style={{ color: "#10b981" }}>{correct}</p>
                      <p className="review-stat-label">Correct</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "#ef4444" }}>{wrong}</p>
                      <p className="review-stat-label">Wrong</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "#f59e0b" }}>{skipped}</p>
                      <p className="review-stat-label">Skipped</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "var(--text-2)" }}>{total}</p>
                      <p className="review-stat-label">Total</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 12 }}>
                    {isPracticeMode
                      ? "Practice scores are not saved. Review your answers below."
                      : `This attempt scored ${percent}% but was not saved.${priorScore?.percent != null ? ` Your recorded score remains ${priorScore.percent}%.` : ""}`}
                  </p>
                </>
              ) : (
                /* ── Assessment mode summary header ── */
                <>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 14px", borderRadius: 9999,
                    background: passed ? "#dcfce7" : "#fee2e2",
                    border: `1px solid ${passed ? "#86efac" : "#fca5a5"}`,
                    color: passed ? "#166534" : "#991b1b",
                    fontSize: 12, fontWeight: 700, letterSpacing: "0.05em",
                    textTransform: "uppercase", marginBottom: 12,
                  }}>
                    {passed ? "✓ Pass" : "✗ Fail"}
                    <span style={{ opacity: 0.6, fontWeight: 500 }}>· {passThreshold}% required</span>
                  </div>

                  <h2 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 4px", color: "var(--text-1)" }}>
                    {passed ? "Well done! 🎉" : "Better luck next time"}
                  </h2>
                  <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 0 }}>{module.title}</p>

                  <div
                    className="review-score-ring"
                    style={{ background: `conic-gradient(${passed ? "#10b981" : "#ef4444"} ${percent * 3.6}deg, #e2e8f0 0deg)` }}
                  >
                    <span style={{ color: passed ? "#065f46" : "#991b1b" }}>{percent}%</span>
                  </div>

                  <div className="review-stats">
                    <div>
                      <p className="review-stat-value" style={{ color: "#10b981" }}>{score}<span style={{ fontSize: 13, fontWeight: 500, color: "#6ee7b7" }}>/{maxPoints}</span></p>
                      <p className="review-stat-label">Points</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "#10b981" }}>{correct}</p>
                      <p className="review-stat-label">Correct</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "#ef4444" }}>{wrong}</p>
                      <p className="review-stat-label">Wrong</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "#f59e0b" }}>{skipped}</p>
                      <p className="review-stat-label">Skipped</p>
                    </div>
                    <div>
                      <p className="review-stat-value" style={{ color: "var(--text-2)" }}>{total}</p>
                      <p className="review-stat-label">Total</p>
                    </div>
                  </div>
                </>
              )}

              <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  className="quiz-btn primary"
                  style={{ opacity: activeFilter === "all" ? 1 : 0.6 }}
                  onClick={() => setActiveFilter("all")}
                >
                  Show All
                </button>
                <button
                  className="quiz-btn"
                  style={{
                    background: activeFilter === "wrong" ? "#fee2e2" : undefined,
                    border: activeFilter === "wrong" ? "1.5px solid #fca5a5" : undefined,
                    color: activeFilter === "wrong" ? "#991b1b" : undefined,
                  }}
                  onClick={() => setActiveFilter(activeFilter === "wrong" ? "all" : "wrong")}
                >
                  Wrong Only
                </button>
                {skipped > 0 && (
                  <button
                    className="quiz-btn"
                    style={{
                      background: activeFilter === "skipped" ? "#fef3c7" : undefined,
                      border: activeFilter === "skipped" ? "1.5px solid #f59e0b" : undefined,
                      color: activeFilter === "skipped" ? "#92400e" : undefined,
                    }}
                    onClick={() => setActiveFilter(activeFilter === "skipped" ? "all" : "skipped")}
                  >
                    Skipped Only
                  </button>
                )}
                <button className="quiz-btn skip" onClick={() => window.location.reload()}>
                  Restart Quiz
                </button>
              </div>
            </div>

            {(() => {
              // Build a unified list: answered entries + skipped placeholders, in question order
              const allEntries = module.questions.map((q, qIdx) => {
                if (userAnswers[qIdx] != null) return userAnswers[qIdx];
                if (finalSkipped.has(qIdx)) return {
                  question: q.question,
                  code: q.code || null,
                  isSkipped: true,
                  isCorrect: false,
                  questionNumber: qIdx + 1,
                  points: parseFloat(q.points ?? defaultPoints),
                  earnedPoints: 0,
                  correct: Array.isArray(q.answer) ? q.answer.join(", ") : q.answer,
                  explanation: q.explanation,
                };
                return null;
              }).filter(Boolean);

              return allEntries
                .filter((a) => {
                  if (activeFilter === "skipped") return a.isSkipped;
                  if (activeFilter === "wrong") return !a.isSkipped && !a.isCorrect;
                  return true;
                })
                .map((ans, idx) => (
                <div key={idx} className="quiz-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--text-3)", margin: 0 }}>
                      Question {ans.questionNumber}
                    </p>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 9999,
                      background: ans.isSkipped ? "#fef3c7" : ans.isCorrect ? "#dcfce7" : "#fee2e2",
                      color: ans.isSkipped ? "#92400e" : ans.isCorrect ? "#166534" : "#991b1b",
                      border: `1px solid ${ans.isSkipped ? "#fcd34d" : ans.isCorrect ? "#86efac" : "#fca5a5"}`,
                    }}>
                      {ans.isSkipped ? "⏭ Skipped" : `${ans.isCorrect ? `+${ans.points}` : "0"} / ${ans.points} pts`}
                    </span>
                  </div>

                  <p className="quiz-question" style={{ marginBottom: ans.code ? 8 : 16 }}>{ans.question}</p>
                  {ans.code && (
                    <pre className="code-block" style={{ marginBottom: 16 }}>{ans.code}</pre>
                  )}

                  {ans.isSkipped ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                      <span style={{ color: "var(--text-2)", fontWeight: 500, minWidth: 100 }}>Correct answer:</span>
                      <span style={{ fontWeight: 600, color: "#065f46", background: "#ecfdf5", padding: "4px 10px", borderRadius: 4, whiteSpace: "pre-wrap" }}>
                        {ans.correct.replace(/\\n/g, "\n")}
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                        <span style={{ color: "var(--text-2)", fontWeight: 500, minWidth: 100 }}>Your answer:</span>
                        {ans.isCoding ? (
                          <pre style={{
                            background: ans.isCorrect ? "#ecfdf5" : "#fef2f2",
                            border: `1px solid ${ans.isCorrect ? "#a7f3d0" : "#fecaca"}`,
                            color: ans.isCorrect ? "#065f46" : "#991b1b",
                            padding: "8px 12px", borderRadius: 6, fontSize: 12,
                            whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0, flex: 1,
                          }}>{ans.selected || "Not answered"}</pre>
                        ) : (
                          <span style={{ fontWeight: 600, color: ans.isCorrect ? "#065f46" : "#991b1b", background: ans.isCorrect ? "#ecfdf5" : "#fef2f2", padding: "4px 10px", borderRadius: 4, whiteSpace: "pre-wrap" }}>
                            {(ans.selected || "Not answered").replace(/\\n/g, "\n")}
                          </span>
                        )}
                      </div>
                      {!ans.isCorrect && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                          <span style={{ color: "var(--text-2)", fontWeight: 500, minWidth: 100 }}>Correct:</span>
                          {ans.isCoding ? (
                            <pre style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", padding: "8px 12px", borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0, flex: 1 }}>{ans.correct}</pre>
                          ) : (
                            <span style={{ fontWeight: 600, color: "#065f46", background: "#ecfdf5", padding: "4px 10px", borderRadius: 4, whiteSpace: "pre-wrap" }}>
                              {ans.correct.replace(/\\n/g, "\n")}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {!ans.isSkipped && !ans.isCorrect && ans.explanation && (
                    <div className="explanation">
                      <strong>Explanation</strong>
                      <p style={{ margin: "4px 0 0" }}>{ans.explanation}</p>
                    </div>
                  )}
                  {ans.isSkipped && ans.explanation && (
                    <div className="explanation" style={{ background: "#fffbeb", borderColor: "#fcd34d" }}>
                      <strong>Explanation</strong>
                      <p style={{ margin: "4px 0 0" }}>{ans.explanation}</p>
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════ QUIZ SCREEN ════════════════════ */
  return (
    <div className="quiz-page">

       {timeWarning === "5min" && (
      <div className="warning-popup-overlay">
        <div className="warning-popup">
          <h3>⏳ Time Reminder</h3>
          <p>Only 5 minutes remaining!</p>
          <button onClick={() => setTimeWarning(null)}>OK</button>
        </div>
      </div>
    )}

      {timeWarning && (
        <div className={`time-warning-toast ${timeWarning === "1min" ? "urgent" : ""}`}>
          {timeWarning === "5min" ? "⏳ 5 minutes remaining!" : "⚠️ Only 1 minute left!"}
        </div>
      )}

      {showConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3 className="confirm-title">{isPracticeMode ? "Finish Practice?" : isRetake ? "Finish Retake?" : "Submit Assessment?"}</h3>
            <p className="confirm-body">
              You still have{" "}
              {skippedQuestions.size > 0 && <><strong>{skippedQuestions.size} skipped</strong></>}
              {skippedQuestions.size > 0 && unansweredCount > 0 && " and "}
              {unansweredCount > 0 && <><strong>{unansweredCount} unanswered</strong></>}
              {" "}{totalPending === 1 ? "question" : "questions"}.
              These will be marked as incorrect.
            </p>
            <div className="confirm-actions">
              <button className="quiz-btn skip" onClick={() => setShowConfirm(false)}>Go Back</button>
              <button className="quiz-btn complete" onClick={finishQuiz}>Submit Anyway</button>
            </div>
          </div>
        </div>
      )}

      <div className="quiz-topbar">
        <span className="quiz-topbar-title">{module.title}</span>
        <div className="quiz-topbar-right">
          {effectivePracticeMode ? (
            <span style={{
              fontSize: 12, fontWeight: 700, padding: "3px 12px", borderRadius: 9999,
              background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
            }}>
              {isPracticeMode ? "🎓 Practice" : "📝 Non-Graded"}
            </span>
          ) : (
            <div className={`quiz-timer ${timeLeft <= 60 ? "danger" : timeLeft <= 300 ? "warn" : ""}`}>
              ⏱ {minutes}:{seconds.toString().padStart(2, "0")}
            </div>
          )}
        </div>
      </div>

      <div className="quiz-progress">
        <div className="quiz-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="quiz-container">
        <div className="quiz-layout">

          <aside className="quiz-sidebar">
            <div className="q-palette-card">
              <p className="q-palette-header">Question Navigator</p>
              <div className="q-palette">
                {module.questions.map((_, idx) => {
                  const isAnswered = !!userAnswers[idx];
                  const isSkipped  = skippedQuestions.has(idx);
                  const isCurrent  = idx === currentQ;
                  return (
                    <span
                      key={idx}
                      onClick={() => isSkipped && navigateToSkipped(idx)}
                      title={isSkipped ? "Click to go to this question" : undefined}
                      className={[
                        "q-dot",
                        isCurrent  ? "q-dot--current"  : "",
                        isAnswered ? "q-dot--answered" : "",
                        isSkipped  ? "q-dot--skipped q-dot--clickable" : "",
                      ].join(" ")}
                    >
                      {idx + 1}
                    </span>
                  );
                })}
              </div>
              <div className="q-legend">
                <span className="q-legend-item"><span className="q-dot q-dot--answered q-dot--sm" />Answered</span>
                <span className="q-legend-item"><span className="q-dot q-dot--skipped  q-dot--sm" />Skipped</span>
                <span className="q-legend-item"><span className="q-dot q-dot--current  q-dot--sm" />Current</span>
                <span className="q-legend-item"><span className="q-dot q-dot--sm" />Not visited</span>
              </div>
              <div className="q-summary">
                <span style={{ color: "#10b981", fontWeight: 700 }}>{answeredCount}</span>
                <span style={{ color: "var(--text-3)" }}> / {module.questions.length} answered</span>
              </div>
              <button
                className="quiz-btn complete"
                style={{ width: "100%", marginTop: 12, justifyContent: "center" }}
                onClick={tryFinish}
              >
                Submit Quiz
              </button>
            </div>
          </aside>

          <div className="quiz-main">
            <div className="quiz-meta">
              <span className="quiz-meta-label">
                Question {currentQ + 1} / {module.questions.length}
              </span>
              <span style={{
                fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 9999,
                background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
              }}>
                {parseFloat(q.points ?? defaultPoints)} pt{parseFloat(q.points ?? defaultPoints) !== 1 ? "s" : ""}
              </span>
              {isRevisiting && <span className="revisit-badge">🔄 Revisiting skipped</span>}
              {!isRevisiting && skippedQuestions.size > 0 && (
                <span className="skipped-badge">{skippedQuestions.size} skipped</span>
              )}
            </div>

            <div className="quiz-card">
              {isRevisiting && (
                <div className="revisit-banner">
                  ⏭ You skipped this earlier — answer it before finishing.
                </div>
              )}

              <p className="quiz-question">{q.question}</p>

              {q.code && q.type !== "coding" && q.type !== "fill-in-blank" && (
                <pre className="code-block">{q.code}</pre>
              )}

              {q.type === "coding" ? (
                /* ── Guess the Output Question ── */
                <div className="coding-question">
                  
                  <pre className="coding-pre">{q.code}</pre>
                  <p className="coding-label" style={{ marginTop: 12 }}>Your Answer</p>
                  <textarea
                    className="coding-editor"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    placeholder="Type the expected output here…"
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    rows={4}
                  />
                  {q.hint && <p className="coding-hint">💡 Hint: {q.hint}</p>}
                  <div className="quiz-actions">
                    <button
                      onClick={submitAnswer}
                      disabled={!codeInput.trim() || !!practiceResult}
                      className="quiz-btn primary"
                    >
                      Submit Answer
                    </button>
                    {!isRevisiting && !practiceResult && (
                      <button onClick={skipQuestion} className="quiz-btn skip">Skip →</button>
                    )}
                  </div>
                </div>

              ) : q.type === "fill-in-blank" ? (
                /* ── Fill-in-the-Blank Question (each blank tracked independently) ── */
                <div className="coding-question">
                  <p className="coding-label">Complete the code</p>
                  <pre className="coding-pre fill-blank-pre">
  {fillTemplate.split("___").map((part, i, arr) => (
    <span key={i}>
      {part}
      {i < arr.length - 1 && (
        <input
          className="fill-blank-input"
          value={blankInputs[i] ?? ""}
          onChange={(e) => updateBlank(i, e.target.value)}
          placeholder="type here"
          spellCheck={false}
          autoComplete="off"
          disabled={!!practiceResult}
        />
      )}
    </span>
  ))}
</pre>
                  {q.hint && (
                    <p className="coding-hint">💡 Hint: {q.hint}</p>
                  )}
                  <div className="quiz-actions">
                    <button
                      onClick={submitAnswer}
                      disabled={blankInputs.length === 0 || blankInputs.some((b) => !b.trim()) || !!practiceResult}
                      className="quiz-btn primary"
                    >
                      Submit Answer
                    </button>
                    {!isRevisiting && !practiceResult && (
                      <button onClick={skipQuestion} className="quiz-btn skip">
                        Skip →
                      </button>
                    )}
                  </div>
                </div>

              ) : (
                /* ── MCQ Question ── */
                <>
                  <div className="options-list">
                    {q.options.map((opt, idx) => {
                      const isSelected = selected === opt || (practiceResult && practiceResult.selected === opt);
                      const isCorrectOpt = Array.isArray(q.answer) ? q.answer.includes(opt) : opt === q.answer;
                      let optClass = "option-btn";
                      if (practiceResult) {
                        if (isCorrectOpt) optClass += " correct";
                        else if (isSelected && !isCorrectOpt) optClass += " incorrect";
                      } else if (isSelected) {
                        optClass += " selected";
                      }
                      return (
                        <button
                          key={idx}
                          onClick={() => !feedback && !practiceResult && setSelected(opt)}
                          disabled={!!feedback || !!practiceResult}
                          className={optClass}
                        >
                          <input type="radio" checked={isSelected} readOnly style={{ accentColor: "var(--accent)" }} />
                          <span style={{ whiteSpace: "pre-wrap" }}>{opt.replace(/\\n/g, "\n")}</span>
                        </button>
                      );
                    })}
                  </div>

                  {!practiceResult && (
                    <div className="quiz-actions">
                      <button onClick={submitAnswer} disabled={selected === null} className="quiz-btn primary">
                        Submit Answer
                      </button>
                      {!isRevisiting && (
                        <button onClick={skipQuestion} className="quiz-btn skip">
                          Skip →
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Practice mode: inline result after submission ── */}
              {effectivePracticeMode && practiceResult && (
                <div style={{
                  marginTop: 16,
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: practiceResult.isCorrect ? "#f0fdf4" : "#fef2f2",
                  border: `1.5px solid ${practiceResult.isCorrect ? "#86efac" : "#fca5a5"}`,
                }}>
                  <p style={{
                    fontWeight: 700, fontSize: 15, margin: "0 0 6px",
                    color: practiceResult.isCorrect ? "#166534" : "#991b1b",
                  }}>
                    {practiceResult.isCorrect ? "✅ Correct!" : "❌ Incorrect"}
                  </p>
                  {!practiceResult.isCorrect && (
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      <span style={{ color: "var(--text-2)", fontWeight: 500 }}>Correct answer: </span>
                      {practiceResult.isCoding ? (
                        <pre style={{
                          display: "inline-block", background: "#ecfdf5", border: "1px solid #a7f3d0",
                          color: "#065f46", padding: "4px 10px", borderRadius: 6, fontSize: 12,
                          whiteSpace: "pre-wrap", wordBreak: "break-all", margin: "4px 0 0",
                        }}>{practiceResult.correct}</pre>
                      ) : (
                        <span style={{ fontWeight: 600, color: "#065f46", background: "#ecfdf5", padding: "3px 10px", borderRadius: 4, whiteSpace: "pre-wrap" }}>
                          {String(practiceResult.correct).replace(/\\n/g, "\n")}
                        </span>
                      )}
                    </div>
                  )}
                  {practiceResult.explanation && (
                    <div style={{
                      fontSize: 13, color: "var(--text-2)", marginTop: 6,
                      paddingTop: 8, borderTop: "1px solid rgba(0,0,0,0.07)",
                    }}>
                      <strong style={{ color: "var(--text-1)" }}>Explanation: </strong>
                      {practiceResult.explanation}
                    </div>
                  )}
                  <button
                    className="quiz-btn primary"
                    style={{ marginTop: 14 }}
                    onClick={practiceNext}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
